import classnames from 'classnames';
import compact from 'lodash/compact';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Reactable from 'reactable';
import { t } from '../../../../modules/infra/i18n';
import Money from '../../../common/money';
import Footer from './components/Footer';
import { extractUnassignedSales, sumReports } from './helpers';
import Hours from '../../../common/Hours';

class PayoutsServiceTable extends Component {
  getColumnTranslations() {
    return {
      'Name': t('reports:payouts.table.name'),
      'Hours Worked': t('reports:payouts.table.hours_worked'),
      'Card': t('reports:payouts.table.card'),
      'Cash': t('reports:payouts.table.cash'),
      'GC Used': t('reports:payouts.table.gc_used'),
      'GC Bought': t('reports:payouts.table.gc_bought'),
      'Total': t('reports:payouts.table.total'),
      'Commission %': t('reports:payouts.table.commission_percent'),
      'Barber Commission': t('reports:payouts.table.barber_commission'),
      'Squire Tips': t('reports:payouts.table.squire_tips'),
      'POS Tips': t('reports:payouts.table.pos_tips'),
      'Cash Tips': t('reports:payouts.table.cash_tips'),
      'Gift Card Tips': t('reports:payouts.table.gift_card_tips'),
      'Tips': t('reports:payouts.table.tips'),
      'Due to Barbers': t('reports:payouts.table.due_to_barbers'),
      'Tax': t('reports:payouts.table.tax'),
      'Due To Shop': t('reports:payouts.table.due_to_shop'),
    };
  }
  renderLargeSingleRow(singleReport) {
    const { Tr } = Reactable;
    const { Td } = Reactable;
    const { showGiftCards, cashTipsEnabled, giftCardTipsEnabled } = this.props;
    return (
      <Tr
        key={`large_${singleReport.Name}`}
        className="c-table-card__row c-table-card__row--clickable"
        data-automation-id={`dataRow:service:${singleReport.Name}`}
      >
        <Td
          column="Name"
          className="c-table-card__cell u-text-left u-pl--"
          data={singleReport.Name}
          data-automation-id="text:name"
        >
          {singleReport.Name}
        </Td>
        <Td
          column="Hours Worked"
          className="c-table-card__cell u-text-left u-pl--"
          value={singleReport['Hours Worked']}
          data-automation-id="text:hoursWorked"
        >
          <Hours>{singleReport['Hours Worked']}</Hours>
        </Td>
        <Td column="Card" value={singleReport.Card} className="c-table-card__cell">
          <Money cents={singleReport.Card} automationId="text:card" />
        </Td>
        <Td column="Cash" value={singleReport.Cash} className="c-table-card__cell">
          <Money cents={singleReport.Cash} automationId="text:cash" />
        </Td>
        <Td
          column="GC Used"
          value={singleReport['Gift Card']}
          className={classnames('c-table-card__cell', { 'is-hidden': !showGiftCards })}
        >
          <Money cents={singleReport['Gift Card']} automationId="text:giftCard" />
        </Td>
        <Td
          column="GC Bought"
          value={singleReport['GC Bought']}
          className={classnames('c-table-card__cell', { 'is-hidden': !showGiftCards })}
        >
          <Money cents={singleReport['GC Bought']} automationId="text:gcBought" />
        </Td>
        <Td column="Total" value={singleReport.Total} className="c-table-card__cell">
          <Money cents={singleReport.Total} automationId="text:total" />
        </Td>
        <Td
          column="Commission %"
          value={singleReport.commissionPercentage}
          className="c-table-card__cell"
          data-automation-id="text:commissionPercentage"
        >
          {singleReport.commissionPercentage && `${singleReport.commissionPercentage}%`}
        </Td>
        <Td column="Barber Commission" value={singleReport['Barber Commission']} className="c-table-card__cell">
          <Money cents={singleReport['Barber Commission']} automationId="text:barberCommission" />
        </Td>
        <Td column="Squire Tips" value={singleReport['Squire Tips']} className="c-table-card__cell">
          <Money cents={singleReport['Squire Tips']} automationId="text:squireTips" />
        </Td>
        <Td column="POS Tips" value={singleReport['POS Tips']} className="c-table-card__cell">
          <Money cents={singleReport['POS Tips']} automationId="text:posTips" />
        </Td>
        <Td
          column="Cash Tips"
          value={singleReport['Cash Tips']}
          className={classnames('c-table-card__cell', { 'is-hidden': !cashTipsEnabled })}
        >
          <Money cents={singleReport['Cash Tips']} automationId="text:cashTips" />
        </Td>
        <Td
          column="Gift Card Tips"
          value={singleReport['Gift Card Tips']}
          className={classnames('c-table-card__cell', { 'is-hidden': !giftCardTipsEnabled })}
        >
          <Money cents={singleReport['Gift Card Tips']} automationId="text:giftCardTips" />
        </Td>
        <Td column="Tips" value={singleReport.Tips} className="c-table-card__cell">
          <Money cents={singleReport.Tips} automationId="text:tips" />
        </Td>
        <Td column="Due to Barbers" value={singleReport['Due to Barbers']} className="c-table-card__cell">
          <Money cents={singleReport['Due to Barbers']} automationId="text:dueToBarbers" />
        </Td>
        <Td column="Tax" value={singleReport.TaxAmount} className="c-table-card__cell">
          <Money cents={singleReport.TaxAmount} automationId="text:taxAmount" />
        </Td>
        <Td column="Due To Shop" value={singleReport['Due To Shop']} className="c-table-card__cell">
          <Money cents={singleReport['Due To Shop']} automationId="text:dueToShop" />
        </Td>
      </Tr>
    );
  }

  render() {
    const { barberReports, showGiftCards, cashTipsEnabled, giftCardTipsEnabled, timeTrackingEnabled } = this.props;
    const translations = this.getColumnTranslations();

    const sortableLarge = compact([
      'Name',
      timeTrackingEnabled ? 'Hours Worked' : null,
      'Card',
      'Cash',
      showGiftCards ? 'GC Used' : null,
      showGiftCards ? 'GC Bought' : null,
      'Total',
      'Commission %',
      'Barber Commission',
      'Squire Tips',
      'POS Tips',
      cashTipsEnabled ? 'Cash Tips' : null,
      giftCardTipsEnabled ? 'Gift Card Tips' : null,
      'Tips',
      'Due to Barbers',
      'Tax',
      'Due To Shop',
    ]);

    // Create columns with key (for Td matching) and label (for display)
    const columnsWithLabels = sortableLarge.map((col) => ({
      key: col,
      label: translations[col] || col,
    }));

    const { Table } = Reactable;
    let report = null;
    const { sales: data, unassigned } = extractUnassignedSales(barberReports);
    if (data.length) {
      report = data.map(barberReport => this.renderLargeSingleRow(barberReport));
    }

    const total = sumReports(barberReports);
    total['GC Used'] = total['Gift Card'];

    return (
      <Table
        className="c-table-new c-table-with-sticky-header c-table--small c-table--border c-no-hover"
        id="table"
        sortable={sortableLarge}
        columns={columnsWithLabels}
        data-automation-id="table:services"
      >
        {report}
        {Footer({ columns: sortableLarge, total, unassigned })}
      </Table>
    );
  }
}

PayoutsServiceTable.propTypes = {
  barberReports: PropTypes.array.isRequired,
  showGiftCards: PropTypes.bool.isRequired,
  cashTipsEnabled: PropTypes.bool.isRequired,
  giftCardTipsEnabled: PropTypes.bool.isRequired,
  timeTrackingEnabled: PropTypes.bool.isRequired,
};

export default PayoutsServiceTable;
